import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import {
  DealStatusFindDto,
  type DealStatusFindInterface,
} from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import {
  ContragentFindDto,
  type ContragentFindInterface,
} from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import {
  CompanyFindDto,
  type CompanyFindInterface,
} from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import {
  DirectionFindDto,
  type DirectionFindInterface,
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { UnitFindDto, type UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import {
  DealsGroupFindDto,
  type DealsGroupFindInterface,
} from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import {
  UserGroupFindDto,
  type UserGroupFindInterface,
} from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import {
  DealSynchronizeHistoryDto,
  type DealSynchronizeHistoryInterface,
} from '@zaoblako/shared/modules/business/dto/deal/deal-synchronize-history-find.dto';
import {
  DocumentsStatisticsFindDto,
  type DocumentsStatisticsFindInterface,
} from '@zaoblako/shared/modules/business/dto/documents-statistics-find.dto';
import {
  ReminderFindDto,
  type ReminderFindInterface,
} from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import {
  DealOrderFindDto,
  type DealOrderFindInterface,
} from '@zaoblako/shared/modules/business/dto/deal-order/deal-order-find.dto';
import {
  ExternalUserFindDto,
  type ExternalUserFindInterface,
} from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import {
  ContractFindDto,
  type ContractFindInterface,
} from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import { RoomFindDto, type RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';
import {
  EavEntityFindDto,
  type EavEntityFindInterface,
} from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-find.dto';
import {
  DealTypeFindDto,
  type DealTypeFindInterface,
} from '@zaoblako/shared/modules/business/dto/deal-type/deal-type.find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';

import * as yup from 'yup';

export interface DealFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Deal creation date and time */
  created: Date;
  /** Deal last update date and time */
  updated: Date;
  /** Deal shipping date and time */
  shippingDate: Date;
  /** Is deal deleted */
  deleted: Date;
  /** Deal display number */
  number: number;
  /** Deal display name */
  name: string;
  /** Deal amount in roubles */
  amount: number;
  /** Deal plan amount in roubles */
  plan: number;
  /** Deal commentary (just some text, maybe formatted) */
  comment?: string;
  /** Accrual method documents: income and expense difference */
  saleDiff?: number;
  /** Number of all files attached to deal */
  filesCount?: number;
  priority?: HelpdeskTaskPriorityEnum;
  /** Planned time to spend for deal in minutes (`0` by default) */
  plannedMin?: number;
  factMin: number;
  /** Deal creator */
  createdBy?: UserFindInterface;
  /** Deal responsible */
  responsible?: UserFindInterface;
  /** Current deal status */
  status: DealStatusFindInterface;
  /** Deal's counterparty */
  contragent?: ContragentFindInterface;
  /** Deal's company */
  company: CompanyFindInterface;
  /** Deal's direction */
  direction: DirectionFindInterface;
  /** Deal's unit */
  unit: UnitFindInterface;
  /** Deal's filial */
  filial: FilialFindInterface;
  /** Deal's contract */
  contract?: ContractFindInterface;
  /** Group this deal belongs to (optional) */
  group?: DealsGroupFindInterface;
  /** Deal type */
  dealType?: DealTypeFindInterface;
  /** User auditors (deals group participants) */
  userAuditors: UserFindInterface[];
  /** Group auditors (deals group participants) */
  groupAuditors: UserGroupFindInterface[];
  /** Deal's 1C ERP databases synchronization history */
  synchronizeHistory?: DealSynchronizeHistoryInterface[];
  /** Documents statistics for this deal */
  documentStatistics: DocumentsStatisticsFindInterface;
  /** Documents statistics for this deal by user */
  documentStatisticsPerUser: DocumentsStatisticsFindInterface[];
  /** Reminder for current authorized user (if any) */
  reminder?: ReminderFindInterface;
  thread?: ThreadFindInterface;
  /** Integration deal ID */
  externalSyncId: string;
  orders?: DealOrderFindInterface[];
  /** Projects to associate with deal */
  projects?: ProjectFindInterface[];
  /** HelpDesk tasks to associate with deal */
  helpdeskTasks?: HelpdeskTaskFindInterface[];
  /** External user relation */
  externalUser?: ExternalUserFindInterface;
  /** EAV entity with additional fields */
  eavEntity?: EavEntityFindInterface;
  // Rent columns
  /** Rent: rented premises */
  rentRooms?: RoomFindInterface[];
  /** Rent: lease start date */
  rentStartDate?: Date;
  /** Rent: rental rate (computed by other rent fields) */
  rentRate?: number;
  /** Rent: corridor factor */
  rentCorridorFactor?: number;
  /** Rent: total area (computed by other rent fields) */
  rentTotalArea?: number;
  /** Rent: deposit (computed by other rent fields) */
  rentDeposit?: number;
  /** Rent: security deposit */
  rentSecurityDeposit?: number;
  /** Rent: operational payment */
  /** Rent: parking lot price */
  rentParkingLotPrice?: number;
  /** Rent amount */
  rentAmount?: number;
}

export const DealFindDto = SerdeDtoClass.create<DealFindInterface>(BusinessDtoKeysEnum.DEAL_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлён',
    serializer: yup.date(),
  },
  shippingDate: {
    label: 'Дата отгрузки',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  number: {
    sortable: true,
    label: 'Номер сделки',
    serializer: yup.number(),
  },
  name: {
    sortable: true,
    label: 'Имя сделки',
    serializer: yup.string(),
  },
  amount: {
    sortable: true,
    label: 'Стоимость',
    serializer: yup.number(),
  },
  plan: {
    sortable: true,
    label: 'План',
    serializer: yup.number(),
  },
  comment: {
    optional: true,
    label: 'Комментарий',
    serializer: yup.string(),
  },
  saleDiff: {
    optional: true,
    label: 'Разница доходов и расходов',
    serializer: yup.number(),
  },
  filesCount: {
    optional: true,
    label: 'Количество файлов',
    serializer: yup.number(),
  },
  priority: {
    optional: true,
    sortable: true,
    label: 'Приоритет',
    serializer: yup
      .string()
      .oneOf(Object.values(HelpdeskTaskPriorityEnum)),
  },
  plannedMin: {
    optional: true,
    label: 'Минимальное планируемое время',
    serializer: yup.number(),
  },
  factMin: {
    label: 'Фактическое время',
    serializer: yup.number(),
  },
  createdBy: {
    optional: true,
    label: 'Создатель',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  status: {
    sortable: true,
    label: 'Статус',
    nested: DealStatusFindDto,
    serializer: (_, nested) => nested,
  },
  contragent: {
    optional: true,
    sortable: true,
    label: 'Контрагент',
    nested: ContragentFindDto,
    serializer: (_, nested) => nested,
  },
  company: {
    label: 'Компания',
    nested: CompanyFindDto,
    serializer: (_, nested) => nested,
  },
  direction: {
    label: 'БС',
    nested: DirectionFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    label: 'Филиал',
    nested: FilialFindDto,
    serializer: (_, nested) => nested,
  },
  contract: {
    label: 'Договор',
    nested: ContractFindDto,
    serializer: (_, nested) => nested,
  },
  unit: {
    label: 'БЕ',
    nested: UnitFindDto,
    serializer: (_, nested) => nested,
  },
  externalUser: {
    optional: true,
    label: 'Внешний пользователь',
    nested: ExternalUserFindDto,
    serializer: (_, nested) => nested,
  },
  group: {
    optional: true,
    sortable: true,
    label: 'Группа',
    nested: DealsGroupFindDto,
    serializer: (_, nested) => nested,
  },
  dealType: {
    optional: true,
    sortable: true,
    label: 'Тип',
    nested: DealTypeFindDto,
    serializer: (_, nested) => nested,
  },
  userAuditors: {
    label: 'Аудиторы',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  groupAuditors: {
    label: 'Группы аудиторов',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  synchronizeHistory: {
    optional: true,
    label: 'История синхронизаций',
    nested: DealSynchronizeHistoryDto,
    serializer: (_, nested) => yup.array(nested),
  },
  documentStatistics: {
    sortable: true,
    label: 'Статистика документов',
    nested: DocumentsStatisticsFindDto,
    serializer: (_, nested) => nested,
  },
  documentStatisticsPerUser: {
    sortable: true,
    label: 'Статистика документов на пользователя',
    nested: DocumentsStatisticsFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  reminder: {
    optional: true,
    label: 'Напоминание',
    nested: ReminderFindDto,
    serializer: (_, nested) => nested,
  },
  thread: {
    optional: true,
    label: 'thread',
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  externalSyncId: {
    label: 'Внешний номер синхронизации',
    serializer: yup.string(),
  },
  orders: {
    optional: true,
    label: 'Заказы',
    nested: DealOrderFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  projects: {
    optional: true,
    label: 'Проекты',
    nested: ProjectDtoKeysEnum.PROJECT_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  helpdeskTasks: {
    optional: true,
    label: 'Заявки HD',
    nested: HelpdeskDtoKeyEnum.HELPDESK_TASK_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  eavEntity: {
    optional: true,
    label: 'Дополнительные поля',
    nested: EavEntityFindDto,
    serializer: (_, nested) => nested,
  },
  rentRooms: {
    optional: true,
    label: 'Аренда: Помещения',
    nested: RoomFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  rentStartDate: {
    optional: true,
    label: 'Аренда: Дата начала аренды',
    serializer: yup.date(),
  },
  rentRate: {
    optional: true,
    label: 'Аренда: Арендная ставка',
    hint: 'Вычисляется по значениям других полей из группы "Аренда"',
    serializer: yup.number(),
  },
  rentCorridorFactor: {
    optional: true,
    label: 'Аренда: Коридорный коэффициент',
    serializer: yup.number(),
  },
  rentTotalArea: {
    optional: true,
    label: 'Аренда: Общая площадь',
    hint: 'Вычисляется по значениям других полей из группы "Аренда"',
    serializer: yup.number(),
  },
  rentDeposit: {
    optional: true,
    label: 'Аренда: Депозит',
    hint: 'Вычисляется по значениям других полей из группы "Аренда"',
    serializer: yup.number(),
  },
  rentSecurityDeposit: {
    optional: true,
    label: 'Аренда: Обеспечительный взнос',
    serializer: yup.number(),
  },
  rentParkingLotPrice: {
    optional: true,
    label: 'Аренда: стоимость парковочного места',
    serializer: yup.number(),
  },
  rentAmount: {
    optional: true,
    label: 'Аренда: Арендная плата',
    serializer: yup.number(),
  },
});
